import { INFRACTION_ATTACHMENT_TYPE } from "../constants/infraction-create.constant";
import type { InfractionAttachmentApiResponse } from "../types/infraction-api.types";

export type InfractionExistingAttachment = {
	id: number;
	type: number;
	fileKey: string | null;
	fileName: string;
	fileSize: number | null;
};

export type InfractionAttachmentSlotKey =
	| "infractionNotice"
	| "penaltyNotification"
	| "receipts";

export type InfractionAttachmentSlots = {
	infractionNotice: InfractionExistingAttachment | null;
	penaltyNotification: InfractionExistingAttachment | null;
	receipts: InfractionExistingAttachment | null;
};

const EMPTY_SLOTS: InfractionAttachmentSlots = {
	infractionNotice: null,
	penaltyNotification: null,
	receipts: null,
};

const SLOT_BY_ATTACHMENT_TYPE: Record<number, InfractionAttachmentSlotKey> = {
	[INFRACTION_ATTACHMENT_TYPE.INFRACTION_NOTICE]: "infractionNotice",
	[INFRACTION_ATTACHMENT_TYPE.PENALTY_NOTIFICATION]: "penaltyNotification",
	[INFRACTION_ATTACHMENT_TYPE.RECEIPTS]: "receipts",
};

export const ATTACHMENT_TYPE_BY_SLOT: Record<
	InfractionAttachmentSlotKey,
	number
> = {
	infractionNotice: INFRACTION_ATTACHMENT_TYPE.INFRACTION_NOTICE,
	penaltyNotification: INFRACTION_ATTACHMENT_TYPE.PENALTY_NOTIFICATION,
	receipts: INFRACTION_ATTACHMENT_TYPE.RECEIPTS,
};

export const ALL_ATTACHMENT_SLOTS: InfractionAttachmentSlotKey[] = [
	"infractionNotice",
	"penaltyNotification",
	"receipts",
];

function toExistingAttachment(
	attachment: InfractionAttachmentApiResponse
): InfractionExistingAttachment {
	return {
		id: attachment.id,
		type: attachment.type,
		fileKey: attachment.fileKey,
		fileName: attachment.fileName as string,
		fileSize: attachment.fileSize,
	};
}

/**
 * Maps detail attachments to form slots by `type` (from GET /infractions/{id}).
 * Only includes attachments with hasFile (fileKey available).
 */
export function mapInfractionAttachmentsToSlots(
	attachments: InfractionAttachmentApiResponse[] | null | undefined
): InfractionAttachmentSlots {
	const slots: InfractionAttachmentSlots = { ...EMPTY_SLOTS };

	for (const attachment of attachments ?? []) {
		if (!(attachment.hasFile && attachment.fileName)) {
			continue;
		}

		const slotKey = SLOT_BY_ATTACHMENT_TYPE[attachment.type];
		if (!slotKey) {
			continue;
		}

		slots[slotKey] = toExistingAttachment(attachment);
	}

	return slots;
}

export function resolveDisplayedAttachmentSlots(
	originalSlots: InfractionAttachmentSlots,
	removedSlotKeys: ReadonlySet<InfractionAttachmentSlotKey>
): InfractionAttachmentSlots {
	return {
		infractionNotice: removedSlotKeys.has("infractionNotice")
			? null
			: originalSlots.infractionNotice,
		penaltyNotification: removedSlotKeys.has("penaltyNotification")
			? null
			: originalSlots.penaltyNotification,
		receipts: removedSlotKeys.has("receipts") ? null : originalSlots.receipts,
	};
}

export type InfractionEditAttachmentFormValues = {
	infractionNoticeFile?: File | null;
	penaltyNotificationFile?: File | null;
	receiptsFile?: File | null;
};

export function getAttachmentFileBySlot(
	values: InfractionEditAttachmentFormValues,
	slotKey: InfractionAttachmentSlotKey
): File | null | undefined {
	if (slotKey === "infractionNotice") {
		return values.infractionNoticeFile;
	}

	if (slotKey === "penaltyNotification") {
		return values.penaltyNotificationFile;
	}

	return values.receiptsFile;
}

export function hasAttachmentDesiredStateChanges(
	values: InfractionEditAttachmentFormValues,
	originalSlots: InfractionAttachmentSlots,
	removedSlotKeys: ReadonlySet<InfractionAttachmentSlotKey>
): boolean {
	for (const slotKey of ALL_ATTACHMENT_SLOTS) {
		if (getAttachmentFileBySlot(values, slotKey)) {
			return true;
		}

		if (removedSlotKeys.has(slotKey) && originalSlots[slotKey]) {
			return true;
		}
	}

	return false;
}

export function isInfractionNoticeMissingAfterRemoval(
	values: {
		infractionNoticeFile?: File | null;
	},
	originalSlots: InfractionAttachmentSlots,
	removedSlotKeys: ReadonlySet<InfractionAttachmentSlotKey>
): boolean {
	if (!originalSlots.infractionNotice) {
		return false;
	}

	if (!removedSlotKeys.has("infractionNotice")) {
		return false;
	}

	return !values.infractionNoticeFile;
}
